import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/**
 * ExportCard — build a data export and either download it or archive it server-side.
 *
 * Drops into AdminTab or ManageTab unchanged; the scope and delivery pickers are
 * driven by /api/export/options, which the server scopes to the caller's role.
 * An instructor sees "All my courses" and download only; an admin additionally
 * sees whole-database scope and the server archive option.
 *
 * Props:
 *   courses — optional [{ id, course_code, course_name }] for the course picker.
 *             Falls back to instructor scope when absent.
 *   admin   — whether to offer the platform-wide options. FALSE in the Manage
 *             tab, true in the Admin tab.
 *
 * The server decides what a role MAY export; this decides what a given tab
 * OFFERS. They aren't the same question. An admin who also teaches is still an
 * admin when they open Manage, so without this they'd be offered "Everything
 * (whole database)" and a server archive path while looking at their own
 * course list — platform tools in a personal context. Those belong in the
 * Admin tab, where the surrounding UI makes clear what they operate on.
 */
export default function ExportCard({ courses = [], admin = false }) {
  const { apiFetch } = useAuth();

  const [options,   setOptions]   = useState(null);
  const [scope,     setScope]     = useState('instructor');
  const [courseId,  setCourseId]  = useState('');
  const [delivery,  setDelivery]  = useState('download');
  const [busy,      setBusy]      = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');
  const [copied,    setCopied]    = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/export/options');
        if (res.ok) setOptions(await res.json());
      } catch { /* ignore */ }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (admin) return;
    if (scope === 'all') setScope('instructor');
    if (delivery === 'server') setDelivery('download');
  }, [admin, scope, delivery]);

  const runExport = async () => {
    setBusy(true); setError(''); setResult(null); setCopied(false);
    try {
      const res = await apiFetch('/api/export/prepare', {
        method: 'POST',
        body: JSON.stringify({
          scope,
          delivery,
          course_id: scope === 'course' && courseId ? Number(courseId) : null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof d.detail === 'string' ? d.detail : 'Export failed.');
        return;
      }
      setResult(d);

      // Downloads go through a second request so the passphrase can come back
      // as JSON — a file response and a JSON body can't be the same response.
      if (d.delivery === 'download' && d.download_url) {
        const file = await apiFetch(d.download_url);
        if (!file.ok) {
          setError('The export was built but the download failed. Try again.');
          return;
        }
        const blob = await file.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url;
        a.download = d.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyPassphrase = async () => {
    try {
      await navigator.clipboard.writeText(result.passphrase);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch { /* clipboard unavailable — the text is selectable anyway */ }
  };

  if (!options) return null;

  // Whole-database scope and server-side archiving are platform operations.
  const availableScopes = (options.scopes || [])
    .filter(o => admin || o.value !== 'all')
    .map(o =>
      // The server labels this "One instructor" for an admin, which reads
      // oddly here — in Manage it can only ever mean your own courses.
      !admin && o.value === 'instructor' ? { ...o, label: 'All my courses' } : o,
    );
  const availableDeliveries = (options.deliveries || [])
    .filter(o => admin || o.value !== 'server');

  const scopeNeedsCourse = scope === 'course';
  const canRun = !busy && (!scopeNeedsCourse || courseId);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Export Data</h3>
      <p style={{ fontSize: 12, color: 'var(--txt-dim)', marginBottom: 12 }}>
        Rosters, competencies, queue history and audit logs as CSVs in a zip.
        Take one before deleting anything — deletes are permanent.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div className={styles.field}>
          <label>What to export</label>
          <select value={scope} onChange={e => setScope(e.target.value)}
            style={selectStyle}>
            {availableScopes.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>Where it goes</label>
          <select value={delivery} onChange={e => setDelivery(e.target.value)}
            style={selectStyle}>
            {availableDeliveries.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      {scopeNeedsCourse && (
        <div className={styles.field}>
          <label>Course</label>
          <select value={courseId} onChange={e => setCourseId(e.target.value)}
            style={selectStyle}>
            <option value="">Choose a course…</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>
                {c.course_code} — {c.course_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {!options.encrypted && (
        <div style={warnBox}>
          ⚠ AES support isn't installed on the server, so exports won't be
          encrypted. Ask your administrator to install <code>pyzipper</code>.
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button className="btn-primary" onClick={runExport} disabled={!canRun}>
          {busy ? 'Building…' : delivery === 'server' ? 'Archive on server' : 'Export and download'}
        </button>
      </div>

      {error && (
        <div className={styles.error} style={{ marginTop: 12 }}>⚠ {error}</div>
      )}

      {result && !error && (
        <div style={{ marginTop: 12 }}>
          <div className={styles.success}>
            ✓ {result.message} — {result.courses.length} course(s),{' '}
            {(result.size_bytes / 1024).toFixed(0)} KB
          </div>

          {result.delivery === 'server' && (
            <div style={{
              fontSize: 12, marginTop: 8, padding: '8px 10px', borderRadius: 6,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              color: 'var(--txt-faint)',
            }}>
              On the server at{' '}
              <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--txt)' }}>
                {result.path}
              </span>
              <br />Collect it with scp; it isn't served over the web.
            </div>
          )}

          {result.passphrase && (
            <div style={{
              marginTop: 8, padding: '10px 12px', borderRadius: 6,
              background: 'var(--bg-card)',
              border: '1px solid var(--amber, #f5a524)55',
            }}>
              <div style={{ fontSize: 12, color: 'var(--txt)', marginBottom: 6 }}>
                Passphrase — shown once, not stored anywhere
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code style={{
                  flex: 1, fontSize: 13, userSelect: 'all', wordBreak: 'break-all',
                  fontFamily: 'var(--font-mono, monospace)', color: 'var(--txt)',
                }}>{result.passphrase}</code>
                <button className="btn-ghost" onClick={copyPassphrase}
                  style={{ fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }}>
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--txt-faint)', marginTop: 6 }}>
                Save it now. Without it the archive can't be opened, and re-running
                the export is the only way to get a new one.
              </div>
            </div>
          )}

          {result.warning && (
            <div style={warnBox}>⚠ {result.warning}</div>
          )}
        </div>
      )}
    </section>
  );
}

const selectStyle = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--txt)', fontSize: 13, padding: '8px 10px', fontFamily: 'inherit',
};

const warnBox = {
  fontSize: 12, marginTop: 10, padding: '8px 10px', borderRadius: 6,
  background: 'var(--amber, #f5a524)15', color: 'var(--txt-dim)',
  border: '1px solid var(--amber, #f5a524)44',
};
